import type React from 'react'
import { useCallback, useMemo } from 'react'

import { type ColumnDef, createColumnHelper, getCoreRowModel, type Row, useReactTable } from '@tanstack/react-table'
import dayjs from 'dayjs'

import { AdminTable } from '@/components/atoms/AdminTable'
import { AdminHeader } from '@/components/organisms/AdminHeader/AdminHeader'
import { RegionSelector } from '@/components/organisms/RegionSelector'
import { type GlobalAssignment, type RegionAssignment } from '@/model/composite/regionAssignments'
import { type AssignmentType } from '@/pages/assignments/useAssignmentsPage'
import { type Region } from '@/state/regions/types'

type AssignmentRow = RegionAssignment | GlobalAssignment

const regionColumnHelper = createColumnHelper<RegionAssignment>()
const globalColumnHelper = createColumnHelper<GlobalAssignment>()

const regionColumns = [
  regionColumnHelper.accessor((row) => row._id, {
    id: 'id',
    header: 'Id',
  }),
  regionColumnHelper.accessor((row) => row.region?.name ?? row.regionId ?? '', {
    id: 'region',
    header: 'Region',
    meta: { classname: 'text-center text-sm', headerClassName: 'text-center text-sm' },
  }),
  regionColumnHelper.accessor((row) => row.timestamp, {
    id: 'timestamp',
    header: 'timestamp',
    meta: { classname: 'text-center' },
    cell: (info) => dayjs(info.getValue()).format('HH:mm:ss'),
  }),
  regionColumnHelper.accessor((row) => row.transportRequests.length, {
    id: 'transportRequests',
    header: 'transport Requests',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  regionColumnHelper.accessor((row) => row.teams.length, {
    id: 'teams',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  regionColumnHelper.accessor((row) => row.teams.filter((t) => !t.currentTransportRequest).length, {
    id: 'availableTeams',
    header: 'available Teams',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  regionColumnHelper.accessor((row) => row.executePlan, {
    id: 'execution',
    header: 'execution',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  regionColumnHelper.accessor((row) => row.realDistancesFetched.length, {
    id: 'dmapi',
    header: 'DM API calls',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
]

const globalColumns = [
  globalColumnHelper.accessor((row) => row._id, {
    id: 'id',
    header: 'Id',
  }),
  globalColumnHelper.accessor((row) => row.regionsCount, {
    id: 'regions',
    header: 'Regions',
    meta: { classname: 'text-center text-sm', headerClassName: 'text-center text-sm' },
    cell: (info) => `${info.getValue()} regions`,
  }),
  globalColumnHelper.accessor((row) => row.timestamp, {
    id: 'timestamp',
    header: 'timestamp',
    meta: { classname: 'text-center' },
    cell: (info) => dayjs(info.getValue()).format('HH:mm:ss'),
  }),
  globalColumnHelper.accessor((row) => row.transportRequests.length, {
    id: 'transportRequests',
    header: 'transport Requests',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  globalColumnHelper.accessor((row) => row.teams.length, {
    id: 'teams',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  globalColumnHelper.accessor((row) => row.teams.filter((t) => !t.currentTransportRequest).length, {
    id: 'availableTeams',
    header: 'available Teams',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  globalColumnHelper.accessor((row) => row.executePlan, {
    id: 'execution',
    header: 'execution',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
  globalColumnHelper.accessor((row) => row.realDistancesFetched.length, {
    id: 'dmapi',
    header: 'DM API calls',
    meta: { classname: 'font-semibold text-black text-center' },
  }),
]

export interface AssignmentsListViewProps {
  assignments: RegionAssignment[] | GlobalAssignment[]
  regions: Region[]
  selectedRegionId?: string
  assignmentType?: AssignmentType
  onRegionChange: (regionId: string | undefined) => void
}

export const AssignmentsListView: React.FC<AssignmentsListViewProps> = ({
  assignments,
  regions,
  selectedRegionId,
  assignmentType = 'region',
  onRegionChange,
}) => {
  const columns = useMemo(
    () => (assignmentType === 'global' ? globalColumns : regionColumns) as Array<ColumnDef<AssignmentRow>>,
    [assignmentType]
  )

  const table = useReactTable<AssignmentRow>({
    data: assignments,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleRowClick = useCallback((row: Row<AssignmentRow>) => {
    window.open(`/assignments/${row.original._id}`, '_blank')
  }, [])

  return (
    <div className="flex h-full flex-col">
      <AdminHeader title="Plans" />
      <div className="flex-1 overflow-auto p-4">
        <div className="flex gap-4">
          <RegionSelector
            regions={regions}
            selectedRegionId={selectedRegionId}
            showGlobalOption
            onRegionChange={onRegionChange}
          />
        </div>
        {assignments.length === 0 ? (
          <div className="pt-4">No assignment</div>
        ) : (
          <AdminTable table={table} showFooter={false} onRowClick={handleRowClick} />
        )}
      </div>
    </div>
  )
}
